import { withQueryParams, type QueryParams } from "utils"
import { getIsUrl } from "./getIsUrl"

/**
 * https://developer.mozilla.org/en-US/docs/Web/Media/Guides/Autoplay
 *
 * Majority of browsers don't allow videos to autoplay with sound, or at all.
 * Trying to force it causes poor UX or weird bugs (ex. WEB-20568)
 * To achieve consistent behavior across browsers, we should use those params:
 * - autoplay - false
 * - muted    - false
 * - loop     - false
 */

export const getVideoLink = (videoLink: string): string => {
  const isURL = getIsUrl(videoLink)

  if (!isURL) {
    console.warn(`Invalid video link: ${videoLink}`)
    return videoLink
  }

  const origin = new URL(videoLink).origin

  if (origin.includes("brightcove")) {
    return getBrightcoveLink(videoLink)
  }
  if (origin.includes("dreambroker")) {
    return getDreambrokerLink(videoLink)
  }
  if (origin.includes("mediaflow")) {
    return getMediaflowLink(videoLink)
  }
  if (origin.includes("sharepoint")) {
    return getMicrosoftSharePointEmbedLink(videoLink)
  }
  if (origin.includes("microsoftstream")) {
    return getMicrosoftStreamLink(videoLink)
  }
  if (origin.includes("ted")) {
    return getTedLink(videoLink)
  }
  if (origin.includes("wistia")) {
    return getWistiaLink(videoLink)
  }
  if (origin.includes("vimeo")) {
    return getVimeoLink(videoLink)
  }
  if (origin.includes("youtube") || origin.includes("youtu.be")) {
    return getYoutubeLink(videoLink)
  }

  console.warn(`Unknown content link for video ${videoLink}`)
  return videoLink
}

/**
 * URL format: ?
 * Embed format: https://players.brightcove.net/:channelId/default_default/index.html?videoId=:videoId
 * Player params: ?
 */
const getBrightcoveLink = (videoLink: string) => {
  return videoLink
}

/**
 * URL format: https://dreambroker.com/channel/:channelId/:videoId
 * Embed format: https://dreambroker.com/channel/:channelId/iframe/:videoId
 * Player params: [NO DOCUMENTATION]
 *
 * https://dreambroker.com/channel/ezcsaxds/hyf0r7b3
 */
const getDreambrokerLink = (videoLink: string) => {
  const queryParams = { autoplay: 0, muted: 0, loop: 0 }

  // Video already is in embed format
  if (videoLink.includes("iframe")) {
    return withQueryParams(videoLink, queryParams)
  }

  const { origin, pathname } = new URL(videoLink)
  const [_, _channel, channelId, videoId] = pathname.split("/")

  const url = `${origin}/channel/${channelId}/iframe/${videoId}`

  return withQueryParams(url, queryParams)
}

/**
 * URL format: ?
 * Embed format: https://SUBDOMAIN.mediaflow.city/embed/:videoId
 * Player params: https://docs.mediaflow.site/video-player/options (not sure if those are applicable to iframe)
 */
const getMediaflowLink = (videoLink: string) => {
  // There is no example of Mediaflow video in our system.
  // Return with no changes as I don't know what the link looks like.
  return videoLink
}

/**
 * URL format: many variations
 * Embed links don't have controls.
 * Player params: [NO DOCUMENTATION]
 */
const getMicrosoftSharePointEmbedLink = (videoLink: string) => {
  return videoLink.replace("stream.aspx", "embed.aspx")
}

/**
 * URL format: https://web.microsoftstream.com/video/:videoId
 * Embed format: https://web.microsoftstream.com/embed/:videoId
 * Player params: default <video> params
 *
 * Microsoft Stream was retired in favour of Stream (on SharePoint),
 * but some customers still have old links to it.
 */
const getMicrosoftStreamLink = (videoLink: string) => {
  const videoCode = videoLink.slice(videoLink.indexOf("video"))
  const url = `https://web.microsoftstream.com/embed/${videoCode}`
  const queryParams = {
    autoplay: false,
    muted: false,
    loop: false,
    showinfo: false,
  }

  return withQueryParams(url, queryParams)
}

/**
 * URL format: https://www.ted.com/talks/:videoId
 * Embed format: https://embed.ted.com/talks/:videoId
 * Player params: [NO DOCUMENTATION]
 */
const getTedLink = (videoLink: string) => {
  const { pathname } = new URL(videoLink)
  const queryParams = { autoplay: 0, loop: 0, muted: 0 }

  // TED's UTM query params break the embed, so we take only the raw pathname
  return withQueryParams(`https://embed.ted.com${pathname}`, queryParams)
}

/**
 * URL format: https://SUBDOMAIN.wistia.com/medias/:videoId
 * Embed format: https://SUBDOMAIN.wistia.com/embed/medias/:videoId
 * Player params: https://docs.wistia.com/docs/embed-options-and-plugins
 */
const getWistiaLink = (videoLink: string) => {
  const url = videoLink.replace("wistia.com/medias", "wistia.com/embed/medias")
  const queryParams = {
    autoPlay: false,
    muted: false,
    endVideoBehavior: "reset",
  }

  return withQueryParams(url, queryParams)
}

/**
 * URL format:
 * - public video   - https://vimeo.com/:videoId
 * - unlisted video - https://vimeo.com/:videoId/:videoHash
 * Embed format: since react-player v3 we don't need it!
 * Player params: https://help.vimeo.com/hc/en-us/articles/12426260232977-About-Player-Parameters
 *
 * ⚠️ Since react-player v3 we no longer have to create the Vimeo embed link
 * ourselves - the library takes care of it. Passing an embed link to an
 * unlisted Vimeo video will fail with "Sorry, we're having a little trouble".
 *
 * Reference for if we ever need to create the embed link manually:
 * https://developer.vimeo.com/player/sdk/basics#passing-to-vimeo-player
 */
const getVimeoLink = (videoLink: string) => {
  const queryParams: QueryParams = {
    autoplay: 0,
    muted: 0,
    loop: 0,
  }

  return withQueryParams(videoLink, queryParams)
}

/**
 * URL format:
 * - https://youtube.com/watch?v=:videoId
 * - https://www.youtube.com/shorts/:videoId
 * - https://youtu.be/:videoId
 * Embed format: https://www.youtube.com/embed/:videoId
 * Player params: https://developers.google.com/youtube/player_parameters#Parameters
 */
const getYoutubeLink = (videoLink: string) => {
  const regExp =
    /.*(?:youtu.be\/|v\/|u\/\w\/|embed\/|shorts\/|watch\?v=)([^#&?]*).*/
  const match = videoLink.match(regExp)
  const videoId = match && match[1]?.length === 11 ? match[1] : false

  const url = `https://www.youtube.com/embed/${videoId}`
  const queryParams = { autoplay: 0, muted: 0, loop: 0 }

  return withQueryParams(url, queryParams)
}
